import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Sidebar from '../../components/SideBar/Sidebar';
import Input from '../../components/Inputs/Input/Input';
import Button from '../../components/Buttons/Button/Button';
import Loading from '../../components/Loading/Loading';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';

import * as Body from './Styles';
import * as defines from '../../../utils/defines/Defines';
import * as Functions from '../../../utils/functions/Functions';
import { State } from '../../redux/store';

import SearchIcon from '../../../assets/icons/Search.svg';
import AddIcon from '../../../assets/icons/add-icon.svg';
import Avatar from '../../../assets/images/avatar.png';

import * as Modals from './Modals/Modals';
import SendInvite from './Modals/SendInvite';
import EditUser from './Modals/EditUser';

type UsuarioProps = {
    idUsuario: string;
    nomeCompleto: string;
    fotoUsuario: string | null;
    ddd: string;
    celular: string;
    email: string;
    tipoTectrol: number;
};

const ManageTectrolUsers: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const inputRef = useRef<HTMLInputElement>(null);

    const userType = useSelector((state: State) => state.userType.userType);
    const acessLevel = useSelector((state: State) => state.acessLevel.acessLevel);

    const [state, setState] = useState({
        hasLength: false,
        hasUser: false,
        modalCreateUser: false,
        modalDelete: false,
        loading: true,
        editUser: false,
    });

    const [userData, setUserData] = useState<{
        data: UsuarioProps[];
        filteredUsers: UsuarioProps[];
        selectedUser?: UsuarioProps;
    }>({
        data: [],
        filteredUsers: [],
    });

    const [userDetails, setUserDetails] = useState({
        nomeCompleto: '',
        email: '',
        ddd: '',
        celular: '',
        imagemUsuario: '',
        idUsuario: '',
        tipoTectrol: 0,
    });

    const fetchUsers = () => {
        setState(prev => ({ ...prev, loading: true }));
        axios.get(`${defines.apiURL}/api/TectrolCRUD?userTypeRequest=${userType}`, { withCredentials: true })
            .then(response => {
                const filteredResponse = response.data.content.filter(
                    (user: UsuarioProps) => user.nomeCompleto !== null
                );
                const sorted = Functions.SortResponse(filteredResponse, 'nomeCompleto');
                setUserData({
                    data: sorted,
                    filteredUsers: sorted,
                });
                setState(prev => ({ ...prev, loading: false }));
            })
            .catch(error => {
                Functions.ResolveErrors(
                    error.response.status,
                    error.response.data.code,
                    error.response.data.message,
                    navigate,
                    dispatch,
                    error.response.data.content
                );
                setState(prev => ({ ...prev, loading: false }));
            });
    };

    useEffect(() => {
        fetchUsers();
    }, [dispatch, navigate]);

    const selectUser = (id: string) => {
        if (id === userDetails.idUsuario && state.hasUser) {
            setState(prev => ({ ...prev, hasUser: false }));
            return;
        }

        setState(prev => ({ ...prev, loading: true }));
        axios.get(
            `${defines.apiURL}/api/TectrolCRUD?id=${Functions.ReplaceSpecialCharacters(id)}&userTypeRequest=${userType}`,
            { withCredentials: true }
        )
        .then(response => {
            const responseData = response.data.content;
            setUserDetails({
                nomeCompleto: responseData.nomeCompleto,
                ddd: responseData.ddd,
                email: responseData.email,
                imagemUsuario: responseData.fotoUsuario,
                celular: responseData.celular,
                idUsuario: id,
                tipoTectrol: responseData.tipoTectrol,
            });
            setUserData(prev => ({ ...prev, selectedUser: responseData }));
            setState(prev => ({ ...prev, hasUser: true, loading: false }));
        })
        .catch(error => {
            Functions.ResolveErrors(
                error.response.status,
                error.response.data.code,
                error.response.data.message,
                navigate,
                dispatch,
                error.response.data.content
            );
            setState(prev => ({ ...prev, loading: false }));
        });
    };

    const filterData = (name: string) => {
        const filtered = name.length === 0 
            ? userData.data 
            : userData.data.filter(item => 
                item.nomeCompleto.toUpperCase().includes(name.toUpperCase())
            );
        
        setUserData(prev => ({ ...prev, filteredUsers: filtered }));
        setState(prev => ({ ...prev, hasLength: name.length > 0 }));
    };

    const updateUser = () => {
        const selectedUser = userData.selectedUser;
        if (!selectedUser) return;

        const updatedFields: Partial<UsuarioProps> = {};
        const { nomeCompleto, email, celular, ddd, tipoTectrol, idUsuario } = userDetails;

        if (email !== selectedUser.email) updatedFields.email = email;
        if (celular !== selectedUser.celular) updatedFields.celular = celular;
        if (ddd !== selectedUser.ddd) updatedFields.ddd = ddd;
        if (nomeCompleto !== selectedUser.nomeCompleto) updatedFields.nomeCompleto = nomeCompleto;

        if (Object.keys(updatedFields).length === 0) return;

        axios.put(
            `${defines.apiURL}/api/UpdateUser?userTypeRequest=${userType}`,
            {
                ...updatedFields,
                idUsuario,
                tipoTectrol,
            },
            { withCredentials: true }
        )
        .then(() => {
            dispatch({
                type: 'ADD_NOTIFICATION',
                status: 1,
                title: '',
                text: 'Dados do usuario alterados com sucesso',
            });
            selectUser(idUsuario);
            fetchUsers();
        })
        .catch(error => {
            Functions.ResolveErrors(
                error.response.status,
                error.response.data.code,
                error.response.data.message,
                navigate,
                dispatch,
                error.response.data.content
            );
        });
    };

    const deleteUser = () => {
        setState(prev => ({ ...prev, loading: true }));
        axios.delete(
            `${defines.apiURL}/api/TectrolCRUD?id=${Functions.ReplaceSpecialCharacters(userDetails.idUsuario)}&userTypeRequest=${userType}`,
            { withCredentials: true }
        )
        .then(() => {
            dispatch({
                type: 'ADD_NOTIFICATION',
                status: 1,
                text: 'Usuário excluido com sucesso',
                title: '',
            });
            setState(prev => ({ 
                ...prev, 
                hasUser: false, 
                modalDelete: false, 
                loading: false 
            }));
            fetchUsers();
        })
        .catch(error => {
            Functions.ResolveErrors(
                error.response.status,
                error.response.data.code,
                error.response.data.message,
                navigate,
                dispatch,
                error.response.data.content
            );
            setState(prev => ({ ...prev, loading: false }));
        });
    };

    const getCargo = (id: number) => {
        const cargoMap: Record<number, string> = {
            [defines.CargoTectrol['Comercial']]: 'Comercial',
            [defines.CargoTectrol['Engenheiro']]: 'Engenharia',
            [defines.CargoTectrol['Suporte']]: 'Suporte',
            [defines.CargoTectrol['Padrão']]: 'Padrão',
        };
        return cargoMap[id] || 'Cargo Indisponível';
    };

    return (
        <Body.Container>
            <Sidebar
                type={userType}
                content={defines.ContentSidebar.MANAGE_USERS}
            />
            <Body.Content style={{ flexDirection: 'row' }}>
                <Body.HalfContent
                    style={state.hasUser ? { width: 'calc(100% - 120px)' } : { width: '100%' }}
                >
                    <Body.Row
                        style={{
                            width: '100%',
                            height: '140px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Breadcrumb />
                        <Body.Row style={{ maxWidth: '90%', minWidth: '50px' }}>
                            <Body.FindContent>
                                <Body.Find
                                    style={state.hasLength ? { width: '300px' } : {}}
                                    onChange={(e) => filterData(e.target.value)}
                                    ref={inputRef}
                                />
                                <Body.Icon
                                    src={SearchIcon}
                                    style={{ cursor: 'text' }}
                                    onClick={() => inputRef.current?.focus()}
                                />
                            </Body.FindContent>
                            {acessLevel === defines.CargoTectrol['Suporte'] && (
                                <Body.HeaderButton
                                    onClick={() => setState(prev => ({ ...prev, modalCreateUser: true }))}
                                >
                                    <Body.Icon
                                        src={AddIcon}
                                        style={{
                                            position: 'relative',
                                            top: 0,
                                            left: 0,
                                        }}
                                    />
                                </Body.HeaderButton>
                            )}
                        </Body.Row>
                    </Body.Row>
                    <Body.Scroll style={{ maxHeight: 'calc(100% - 120px)' }}>
                        {userData.filteredUsers.length === 0 ? (
                            <Body.Text>Nenhum usuário encontrado</Body.Text>
                        ) : (
                            userData.filteredUsers.map((item) => (
                                <Body.SelectUser
                                    key={item.idUsuario}
                                    onClick={() => selectUser(item.idUsuario)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Body.Photo src={item.fotoUsuario || Avatar} />
                                        <Body.Text>{item.nomeCompleto}</Body.Text>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <Body.NoteText>{getCargo(item.tipoTectrol)}</Body.NoteText>
                                    </div>
                                </Body.SelectUser>
                            ))
                        )}
                    </Body.Scroll>
                </Body.HalfContent>
                
                {state.hasUser && (
                    <Body.HalfContent style={{ alignItems: 'center', width: '100px' }}>
                        <Body.Photo
                            src={userDetails.imagemUsuario || Avatar}
                            style={{
                                width: '120px',
                                borderRadius: '60px',
                                height: '120px',
                            }}
                        />
                        <Body.Scroll
                            style={{
                                height: '100%',
                                marginBottom: '10px',
                                maxHeight: 'calc(100vh - 80px)',
                                display: 'flex',
                                flexDirection: 'column',
                                gridGap: '20px 0px',
                            }}
                        >
                            <Input
                                text={userDetails.nomeCompleto}
                                setText={(val: string) => setUserDetails(prev => ({ ...prev, nomeCompleto: val }))}
                                title="Nome Completo"
                                disabled={acessLevel !== defines.CargoTectrol['Suporte']}
                            />
                            <Input
                                text={userDetails.email}
                                setText={(val: string) => setUserDetails(prev => ({ ...prev, email: val }))}
                                title="Email"
                                disabled={acessLevel !== defines.CargoTectrol['Suporte']}
                            />
                            <Body.Row>
                                <div style={{ width: '20%' }}>
                                    <Input
                                        text={userDetails.ddd}
                                        setText={(val: string) => setUserDetails(prev => ({ ...prev, ddd: val }))}
                                        title="DDD"
                                        limit={2}
                                        disabled={acessLevel !== defines.CargoTectrol['Suporte']}
                                    />
                                </div>
                                <div style={{ width: '79%' }}>
                                    <Input
                                        text={userDetails.celular}
                                        setText={(val: string) => setUserDetails(prev => ({ ...prev, celular: val }))}
                                        title="Telefone"
                                        disabled={acessLevel !== defines.CargoTectrol['Suporte']}
                                    />
                                </div>
                            </Body.Row>
                        </Body.Scroll>
                        {acessLevel === defines.CargoTectrol['Suporte'] && (
                            <>
                                <Body.Row>
                                    <Button
                                        text="Alterar Tipo"
                                        color="yellow"
                                        action={() => setState(prev => ({ ...prev, editUser: true }))}
                                        width={320}
                                    />
                                </Body.Row>
                                <Body.Row>
                                    <Button
                                        text="Salvar"
                                        action={updateUser}
                                        width={155}
                                    />
                                    <Button
                                        text="Apagar usuário"
                                        action={() => setState(prev => ({ ...prev, modalDelete: true }))}
                                        width={155}
                                        color="red"
                                    />
                                </Body.Row>
                            </>
                        )}
                    </Body.HalfContent>
                )}
            </Body.Content>

            {state.modalDelete && (
                <Modals.ModalDeleteUser
                    toggle={() => setState(prev => ({ ...prev, modalDelete: false }))}
                    idUsuario={userDetails.idUsuario}
                    nome={userDetails.nomeCompleto}
                    action={deleteUser}
                />
            )}

            {state.loading && <Loading />}

            {state.editUser && userData.selectedUser && (
                <EditUser
                    toggle={() => setState(prev => ({ ...prev, editUser: false }))}
                    usuario={userData.selectedUser}
                    regenerate={fetchUsers}
                />
            )}

            {state.modalCreateUser && (
                <SendInvite toggle={() => setState(prev => ({ ...prev, modalCreateUser: false }))} />
            )}
        </Body.Container>
    );
};

export default ManageTectrolUsers;